/*
 * Runtime contract for the Basic 1 home screen wrapper:
 * - root: [data-og-screen][data-section-id]
 * - screen JSON: <script type="application/json" data-og-screen-config>
 * - animation targets: [data-og-animate="logo|menu|social"] with optional
 *   [data-og-animation-order]
 *
 * The 3D object is not this file's concern. It mounts and tears itself down
 * from assets/model-3d.js, so a screen without one loads no WebGL code at all.
 *
 * Public API: window.HomeOgScreen.init(rootOrSectionId), initAll(scope),
 * destroy(rootOrSectionId), and get(rootOrSectionId).
 */
(function () {
  'use strict';

  if (window.HomeOgScreen) return;

  const ROOT_SELECTOR = '[data-og-screen]';
  const SCREEN_CONFIG_SELECTOR = '[data-og-screen-config]';
  const ANIMATION_SELECTOR = '[data-og-animate]';
  const controllers = new WeakMap();
  const liveControllers = new Set();
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function parseConfig(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return {};

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('OG home screen: invalid configuration JSON.', error);
      return {};
    }
  }

  function numberValue(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function booleanValue(value, fallback) {
    return typeof value === 'boolean' ? value : fallback;
  }

  function collectAnimationElements(root, kind) {
    return Array.from(root.querySelectorAll(ANIMATION_SELECTOR))
      .filter((element) => {
        const kinds = element.dataset.ogAnimate.split(/\s+/);
        return kind === 'all' || kinds.includes(kind);
      })
      .sort((first, second) => {
        const firstOrder = numberValue(first.dataset.ogAnimationOrder, 0);
        const secondOrder = numberValue(second.dataset.ogAnimationOrder, 0);
        return firstOrder - secondOrder;
      });
  }

  class EntranceController {
    constructor(root, config) {
      this.root = root;
      this.config = config || {};
      this.animations = [];
      this.timerIds = [];
      this.connected = false;
    }

    connect() {
      if (this.connected) return;
      this.connected = true;
      this.run();
    }

    run() {
      const config = this.config;
      const duration = Math.max(0, numberValue(config.duration, 300));
      const delayStep = Math.max(0, numberValue(config.delay, 100));
      const reducedMotion = reducedMotionQuery.matches;
      const master = booleanValue(config.master, false);
      const groups = master
        ? [{ kind: 'all', enabled: true }]
        : [
            { kind: 'logo', enabled: booleanValue(config.logo, false) },
            { kind: 'menu', enabled: booleanValue(config.menu, false) },
            { kind: 'social', enabled: booleanValue(config.social, false) }
          ];

      const animatedElements = new Set();

      groups.forEach((group) => {
        if (!group.enabled) return;

        collectAnimationElements(this.root, group.kind).forEach((element, index) => {
          if (animatedElements.has(element)) return;
          animatedElements.add(element);

          if (reducedMotion || typeof element.animate !== 'function') {
            element.style.opacity = '1';
            return;
          }

          element.style.opacity = '0';
          const animation = element.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            {
              duration,
              delay: index * delayStep,
              easing: 'ease',
              fill: 'forwards'
            }
          );
          this.animations.push(animation);
        });
      });

      this.root.dataset.ogAnimationsReady = 'true';
    }

    destroy() {
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      this.timerIds.forEach((timerId) => window.clearTimeout(timerId));
      this.timerIds = [];
      this.root.querySelectorAll(ANIMATION_SELECTOR).forEach((element) => {
        element.style.removeProperty('opacity');
      });
      delete this.root.dataset.ogAnimationsReady;
      this.connected = false;
    }

    setReducedMotion(reducedMotion) {
      if (!reducedMotion) return;
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      this.root.querySelectorAll(ANIMATION_SELECTOR).forEach((element) => {
        element.style.opacity = '1';
      });
    }
  }

  class DrilldownController {
    constructor(root) {
      this.root = root;
      this.nav = root.querySelector('[data-og-menu-drilldown]');
      this.container = this.nav ? this.nav.closest('.container') : null;
      this.menuColumn = this.nav ? this.nav.parentElement : null;
      this.panels = this.nav
        ? Array.from(this.nav.querySelectorAll('[data-og-menu-panel]'))
        : [];
      this.rootPanel = this.nav
        ? this.nav.querySelector('[data-og-menu-root]')
        : null;
      this.stack = [];
      this.animations = new Set();
      this.operation = 0;
      this.transitioning = false;
      this.connected = false;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connect() {
      if (
        this.connected ||
        !this.nav ||
        !this.container ||
        !this.menuColumn ||
        !this.rootPanel
      ) {
        return;
      }

      this.connected = true;
      this.menuColumn.dataset.ogMenuColumn = '';
      this.ambient = [
        ...Array.from(this.container.children).filter(
          (element) => element !== this.menuColumn
        ),
        ...Array.from(this.menuColumn.children).filter(
          (element) => element !== this.nav
        )
      ];
      this.stack = [{ panel: this.rootPanel, trigger: null }];
      this.showOnly(this.rootPanel);
      this.nav.addEventListener('click', this.onClick);
      this.root.addEventListener('keydown', this.onKeydown);
    }

    onClick(event) {
      const openButton = event.target.closest('[data-og-menu-open]');
      if (openButton && this.nav.contains(openButton)) {
        event.preventDefault();
        this.open(openButton);
        return;
      }

      const backButton = event.target.closest('[data-og-menu-back]');
      if (backButton && this.nav.contains(backButton)) {
        event.preventDefault();
        this.back();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || this.stack.length < 2) return;
      event.preventDefault();
      this.back();
    }

    showOnly(target) {
      this.panels.forEach((panel) => {
        const active = panel === target;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
    }

    firstControl(panel) {
      return panel.querySelector('a[href], button:not([disabled])');
    }

    animateOpacity(elements, from, to, duration) {
      const targets = elements.filter((element) => element && !element.hidden);
      if (
        reducedMotionQuery.matches ||
        typeof Element.prototype.animate !== 'function' ||
        duration === 0
      ) {
        return Promise.resolve();
      }

      return Promise.all(
        targets.map((element) => {
          const animation = element.animate(
            [{ opacity: from }, { opacity: to }],
            {
              duration,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
              fill: 'forwards'
            }
          );
          this.animations.add(animation);
          return animation.finished
            .catch(() => {})
            .finally(() => {
              this.animations.delete(animation);
              animation.cancel();
            });
        })
      );
    }

    async morphCard(updateLayout) {
      // Drilldown changes links inside the band; it never resizes the strip.
      if (this.root.dataset.ogAxis === 'strip') {
        updateLayout();
        return;
      }
      const start = this.container.getBoundingClientRect();
      this.container.style.boxSizing = 'border-box';
      this.container.style.width = `${start.width}px`;
      this.container.style.height = `${start.height}px`;
      this.container.style.overflow = 'clip';

      updateLayout();

      this.container.style.removeProperty('width');
      this.container.style.removeProperty('height');
      this.container.style.removeProperty('box-sizing');
      const target = this.container.getBoundingClientRect();

      this.container.style.boxSizing = 'border-box';
      this.container.style.width = `${start.width}px`;
      this.container.style.height = `${start.height}px`;

      if (
        reducedMotionQuery.matches ||
        typeof this.container.animate !== 'function'
      ) {
        this.clearCardStyles();
        return;
      }

      const animation = this.container.animate(
        [
          { width: `${start.width}px`, height: `${start.height}px` },
          { width: `${target.width}px`, height: `${target.height}px` }
        ],
        {
          duration: 320,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards'
        }
      );
      this.animations.add(animation);
      await animation.finished.catch(() => {});
      this.animations.delete(animation);
      animation.cancel();
      this.clearCardStyles();
    }

    clearCardStyles() {
      this.container.style.removeProperty('box-sizing');
      this.container.style.removeProperty('width');
      this.container.style.removeProperty('height');
      this.container.style.removeProperty('overflow');
    }

    async open(trigger) {
      if (this.transitioning) return;
      const target = this.nav.querySelector(
        `#${CSS.escape(trigger.dataset.ogMenuOpen)}`
      );
      if (!target || target === this.stack[this.stack.length - 1].panel) return;

      this.transitioning = true;
      const operation = ++this.operation;
      this.root.dataset.ogMenuTransitioning = '';
      const current = this.stack[this.stack.length - 1].panel;
      const entering = this.stack.length === 1;
      const outgoing = entering ? [current, ...this.ambient] : [current];

      await this.animateOpacity(outgoing, 1, 0, 160);
      if (!this.connected || operation !== this.operation) return;
      trigger.setAttribute('aria-expanded', 'true');
      this.stack.push({ panel: target, trigger });

      const morph = this.morphCard(() => {
        if (entering) {
          this.root.dataset.ogMenuActive = '';
          this.ambient.forEach((element) => {
            element.hidden = true;
          });
        }
        this.showOnly(target);
      });
      const firstControl = this.firstControl(target);
      if (firstControl) firstControl.focus({ preventScroll: true });
      await Promise.all([morph, this.animateOpacity([target], 0, 1, 220)]);
      if (!this.connected || operation !== this.operation) return;

      this.finishTransition();
    }

    async back() {
      if (this.transitioning || this.stack.length < 2) return;

      this.transitioning = true;
      const operation = ++this.operation;
      this.root.dataset.ogMenuTransitioning = '';
      const currentEntry = this.stack[this.stack.length - 1];
      const parentEntry = this.stack[this.stack.length - 2];
      const returningRoot = this.stack.length === 2;

      await this.animateOpacity([currentEntry.panel], 1, 0, 160);
      if (!this.connected || operation !== this.operation) return;
      this.stack.pop();
      currentEntry.trigger.setAttribute('aria-expanded', 'false');

      const morph = this.morphCard(() => {
        this.showOnly(parentEntry.panel);
        if (returningRoot) {
          delete this.root.dataset.ogMenuActive;
          this.ambient.forEach((element) => {
            element.hidden = false;
          });
        }
      });
      currentEntry.trigger.focus({ preventScroll: true });
      const incoming = returningRoot
        ? [parentEntry.panel, ...this.ambient]
        : [parentEntry.panel];
      await Promise.all([morph, this.animateOpacity(incoming, 0, 1, 220)]);
      if (!this.connected || operation !== this.operation) return;

      this.finishTransition();
    }

    finishTransition() {
      delete this.root.dataset.ogMenuTransitioning;
      this.transitioning = false;
    }

    setReducedMotion(reducedMotion) {
      if (!reducedMotion) return;
      this.animations.forEach((animation) => animation.cancel());
    }

    destroy() {
      if (!this.connected) return;
      this.operation += 1;
      this.nav.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.animations.forEach((animation) => animation.cancel());
      this.animations.clear();
      this.nav.querySelectorAll('[data-og-menu-open]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.ambient.forEach((element) => {
        element.hidden = false;
      });
      this.showOnly(this.rootPanel);
      delete this.menuColumn.dataset.ogMenuColumn;
      delete this.root.dataset.ogMenuActive;
      delete this.root.dataset.ogMenuTransitioning;
      this.clearCardStyles();
      this.stack = [];
      this.transitioning = false;
      this.connected = false;
    }
  }

  class ScreenController {
    constructor(root) {
      this.root = root;
      this.screenConfig = parseConfig(root, SCREEN_CONFIG_SELECTOR);
      this.entrance = new EntranceController(root, this.screenConfig.animations);
      this.drilldown = new DrilldownController(root);
      this.cornerNav = root.querySelector('[data-og-corner-nav]');
      this.menu = root.querySelector('[data-og-switch-content]');
      this.onMenuVisibility = (event) => {
        const button = event.target.closest('[data-og-menu-visibility]');
        if (!button || !this.cornerNav.contains(button)) return;
        this.setMenuVisible(button.dataset.ogMenuVisibility === 'show');
      };
      this.onBlockSelect = (event) => {
        if (this.menu && this.menu.contains(event.target)) this.setMenuVisible(true);
      };
      this.connected = false;
    }

    connect() {
      if (this.connected) return this;
      this.connected = true;
      this.root.dataset.ogScreenConnected = 'true';
      this.entrance.connect();
      this.drilldown.connect();
      if (this.cornerNav && this.menu) {
        this.cornerNav.hidden = false;
        this.cornerNav.addEventListener('click', this.onMenuVisibility);
        this.root.addEventListener('shopify:block:select', this.onBlockSelect);
      }
      return this;
    }

    destroy() {
      if (!this.connected) return;
      if (this.cornerNav) {
        this.cornerNav.removeEventListener('click', this.onMenuVisibility);
        this.cornerNav.hidden = true;
      }
      this.root.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.setMenuVisible(true);
      this.drilldown.destroy();
      this.entrance.destroy();
      delete this.root.dataset.ogScreenConnected;
      this.connected = false;
    }

    setMenuVisible(visible) {
      if (!this.menu || !this.cornerNav) return;
      // Complete the whole card entrance: SHOW is instant even during page load.
      this.entrance.animations.forEach((animation) => {
        if (animation.effect && this.menu.contains(animation.effect.target)) animation.finish();
      });
      if (!visible && this.menu.contains(document.activeElement)) {
        this.cornerNav.querySelector('[data-og-menu-visibility="show"]').focus();
      }
      this.root.toggleAttribute('data-og-menu-hidden', !visible);
      this.menu.inert = !visible;
      this.menu.setAttribute('aria-hidden', String(!visible));
      this.cornerNav.querySelectorAll('[data-og-menu-visibility]').forEach((button) => {
        button.setAttribute('aria-pressed', String((button.dataset.ogMenuVisibility === 'show') === visible));
      });
    }

    setReducedMotion(reducedMotion) {
      this.entrance.setReducedMotion(reducedMotion);
      this.drilldown.setReducedMotion(reducedMotion);
    }
  }

  function resolveRoot(rootOrId) {
    if (rootOrId instanceof Element) {
      return rootOrId.matches(ROOT_SELECTOR) ? rootOrId : rootOrId.closest(ROOT_SELECTOR);
    }

    if (typeof rootOrId === 'string') {
      return document.querySelector(
        `${ROOT_SELECTOR}[data-section-id="${CSS.escape(rootOrId)}"]`
      );
    }

    return null;
  }

  function init(rootOrId) {
    const root = resolveRoot(rootOrId);
    if (!root) return null;

    let controller = controllers.get(root);
    if (!controller) {
      controller = new ScreenController(root);
      controllers.set(root, controller);
      liveControllers.add(controller);
    }

    return controller.connect();
  }

  function initAll(scope) {
    const searchScope = scope instanceof Element || scope instanceof Document ? scope : document;
    const roots = [];

    if (searchScope instanceof Element && searchScope.matches(ROOT_SELECTOR)) {
      roots.push(searchScope);
    }

    searchScope.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots.map((root) => init(root)).filter(Boolean);
  }

  function destroy(rootOrId) {
    const root = resolveRoot(rootOrId);
    if (!root) return;

    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
    liveControllers.delete(controller);
  }

  function onSectionLoad(event) {
    initAll(event.target);
  }

  function onSectionUnload(event) {
    if (event.target instanceof Element && event.target.matches(ROOT_SELECTOR)) {
      destroy(event.target);
    }
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => destroy(root));
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  reducedMotionQuery.addEventListener('change', (event) => {
    liveControllers.forEach((controller) => controller.setReducedMotion(event.matches));
  });

  window.HomeOgScreen = Object.freeze({
    init,
    initAll,
    destroy,
    get(rootOrId) {
      const root = resolveRoot(rootOrId);
      return root ? controllers.get(root) || null : null;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
